import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Copy,
  Star,
  StarOff,
  ToggleLeft,
  ToggleRight,
  FileText,
  Bug,
  HelpCircle,
  Lightbulb,
  Settings,
  AlertCircle,
  ListFilter,
  Brain,
} from 'lucide-react';
import { Button, Badge, Spinner, Modal, useToast } from '@centraoneplus/ui';
import { ticketTypesService, CreateTicketTypeDto, UpdateTicketTypeDto } from '@services/ticket-types.service';
import type { TicketType } from '@centraoneplus/shared-types';
import { FieldConfigurationPanel, FieldGroupConfig } from './FieldConfigurationPanel';
import { ClassificationConfigEditor, ClassificationConfig } from './ClassificationConfigEditor';
import { pipelineService } from '@/services/pipeline.service';
import type { Pipeline } from '@/types/pipeline';
import { ThemedSelect } from '@components/common/ThemedSelect';
import { ColorPicker } from '@components/common/ColorPicker';

// Available icons for ticket types
const TICKET_TYPE_ICONS = [
  { value: 'file-text', label: 'Document', icon: FileText },
  { value: 'bug', label: 'Bug', icon: Bug },
  { value: 'help-circle', label: 'Help', icon: HelpCircle },
  { value: 'lightbulb', label: 'Idea', icon: Lightbulb },
  { value: 'settings', label: 'Settings', icon: Settings },
  { value: 'alert-circle', label: 'Alert', icon: AlertCircle },
];

// Preset colors for ticket types
const TICKET_TYPE_COLORS = [
  { value: '#3b82f6', label: 'Blue' },
  { value: '#10b981', label: 'Green' },
  { value: '#f59e0b', label: 'Amber' },
  { value: '#ef4444', label: 'Red' },
  { value: '#8b5cf6', label: 'Purple' },
  { value: '#ec4899', label: 'Pink' },
  { value: '#06b6d4', label: 'Cyan' },
  { value: '#6b7280', label: 'Gray' },
];

function getIconComponent(iconName?: string) {
  const iconDef = TICKET_TYPE_ICONS.find((i) => i.value === iconName);
  return iconDef?.icon || FileText;
}

// Tab types for the editor
type EditorTab = 'basic' | 'fields' | 'ai';

// Editor component for creating/editing ticket types with field configuration
interface TicketTypeEditorProps {
  ticketType?: TicketType;
  onSubmit: (data: CreateTicketTypeDto | UpdateTicketTypeDto, fieldGroups: FieldGroupConfig[]) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
  initialTab?: EditorTab;
}

function TicketTypeEditor({ ticketType, onSubmit, onCancel, isLoading, initialTab = 'basic' }: TicketTypeEditorProps) {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<EditorTab>(initialTab);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [formData, setFormData] = useState<CreateTicketTypeDto>({
    name: ticketType?.name || '',
    slug: ticketType?.slug || '',
    description: ticketType?.description || '',
    icon: ticketType?.icon || 'file-text',
    color: ticketType?.color || '#3b82f6',
    isActive: ticketType?.isActive ?? true,
    isDefault: ticketType?.isDefault ?? false,
    ticketPrefix: ticketType?.ticketPrefix || '',
    defaultPipelineId: (ticketType as any)?.defaultPipelineId || undefined,
    fallbackKeywords: (ticketType as any)?.fallbackKeywords || [],
    classificationConfig: (ticketType as any)?.classificationConfig || {},
    isCatchAll: (ticketType as any)?.isCatchAll ?? false,
  });
  const [keywordInput, setKeywordInput] = useState('');

  // Load pipelines for the dropdown
  useEffect(() => {
    pipelineService
      .getPipelines()
      .then(setPipelines)
      .catch((err) => {
        console.error(err);
        toast.error(
          'Failed to load pipelines',
          (err as any)?.response?.data?.message ||
            (err as any)?.response?.data?.error?.message ||
            (err as any)?.message ||
            'Please try again.'
        );
      });
  }, []);
  // Initialize field groups from existing ticket type (only once on mount)
  const getInitialFieldGroups = (): FieldGroupConfig[] => {
    if (ticketType?.fieldGroups) {
      console.log('[TicketTypeEditor] Loading fieldGroups from ticketType:', ticketType.fieldGroups);
      return ticketType.fieldGroups.map((ttfg: any) => {
        // The API returns TicketTypeFieldGroup with nested fieldGroup object
        const fg = ttfg.fieldGroup || ttfg; // Handle both nested and flat structure
        return {
          id: ttfg.id, // Use the TicketTypeFieldGroup ID
          key: fg.key,
          name: fg.name,
          description: fg.description,
          icon: fg.icon,
          displayLocation: fg.displayLocation || 'main',
          isCollapsible: fg.isCollapsible ?? true,
          isCollapsedByDefault: fg.isCollapsedByDefault ?? false,
          sortOrder: ttfg.sortOrder || 0,
          fields: (ttfg.fields || []).map((field: any) => ({
            fieldDefinitionId: field.fieldDefinitionId || field.id,
            fieldDefinition: field.fieldDefinition || field,
            isRequired: field.isRequired ?? false,
            isVisible: field.isVisible ?? true,
            isVisibleInList: field.isVisibleInList ?? false,
            sortOrder: field.sortOrder || 0,
          })),
        };
      });
    }
    return [];
  };

  const [fieldGroups, setFieldGroups] = useState<FieldGroupConfig[]>(getInitialFieldGroups);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (formData.ticketPrefix && !/^[A-Z]{2,5}$/.test(formData.ticketPrefix)) {
      newErrors.ticketPrefix = 'Prefix must be 2-5 uppercase letters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    console.log('[TicketTypeEditor] handleSubmit called');
    console.log('[TicketTypeEditor] fieldGroups state:', fieldGroups.length, fieldGroups);
    if (!validate()) {
      console.log('[TicketTypeEditor] Validation failed, staying on basic tab');
      setActiveTab('basic');
      return;
    }
    console.log('[TicketTypeEditor] Calling onSubmit with formData and fieldGroups');
    await onSubmit(formData, fieldGroups);
  };

  const handleNameChange = (name: string) => {
    setFormData({
      ...formData,
      name,
      // Auto-generate slug for new ticket types
      slug: ticketType
        ? formData.slug
        : name
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-'),
    });
  };

  const Icon = getIconComponent(formData.icon);

  const tabs = [
    { id: 'basic' as const, label: 'Basic Info', icon: Settings },
    { id: 'fields' as const, label: 'Custom Fields', icon: ListFilter },
    { id: 'ai' as const, label: 'AI Classification', icon: Brain },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Preview Header */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-4">
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${formData.color}20`, color: formData.color }}
        >
          <Icon className="w-6 h-6" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {formData.name || 'Ticket Type Name'}
          </p>
          <p className="text-sm text-gray-500 truncate dark:text-gray-400">{formData.description || 'Description'}</p>
        </div>
        {fieldGroups.length > 0 && (
          <Badge variant="secondary" size="sm">
            {fieldGroups.reduce((acc, g) => acc + g.fields.length, 0)} fields
          </Badge>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
        <nav className="flex gap-4">
          {tabs.map((tab) => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <TabIcon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {activeTab === 'basic' && (
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g., Bug Report"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Slug
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="bug-report"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  disabled={!!ticketType}
                />
                {ticketType && (
                  <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">Slug cannot be changed after creation</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ticket Prefix
                </label>
                <input
                  type="text"
                  value={formData.ticketPrefix}
                  onChange={(e) =>
                    setFormData({ ...formData, ticketPrefix: e.target.value.toUpperCase() })
                  }
                  placeholder="BUG"
                  maxLength={5}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                {errors.ticketPrefix && (
                  <p className="mt-1 text-xs text-red-500">{errors.ticketPrefix}</p>
                )}
                <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">e.g., BUG-001, FEAT-001</p>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="A brief description of this ticket type..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Icon Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Icon
              </label>
              <div className="flex flex-wrap gap-2">
                {TICKET_TYPE_ICONS.map((iconOpt) => {
                  const IconComp = iconOpt.icon;
                  return (
                    <button
                      key={iconOpt.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon: iconOpt.value })}
                      className={`p-3 rounded-lg border-2 transition-all ${
                        formData.icon === iconOpt.value
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                      title={iconOpt.label}
                    >
                      <IconComp
                        className="w-5 h-5"
                        style={{ color: formData.icon === iconOpt.value ? formData.color : undefined }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection — the eight presets stay as the quick path, and
                the picker beside them is the way to any other colour. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Color
              </label>
              <ColorPicker
                /* The same fallback the form initialises with — the picker
                   always has a colour to show, even if the stored type has
                   none yet. Displaying it does not write it. */
                value={formData.color || '#3b82f6'}
                onChange={(hex) => setFormData({ ...formData, color: hex })}
                presets={TICKET_TYPE_COLORS}
                label="Ticket type colour"
              />
            </div>

            {/* Options */}
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-700"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 dark:border-gray-700"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Set as default</span>
              </label>
            </div>

            {/* Default Pipeline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Default Pipeline
              </label>
              <ThemedSelect
                value={formData.defaultPipelineId || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  defaultPipelineId: e.target.value || undefined,
                })}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              >
                <option value="">No default pipeline</option>
                {pipelines.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </ThemedSelect>
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                Tickets of this type will be assigned to this pipeline
              </p>
            </div>

            {/* AI Fallback Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                AI Fallback Keywords
              </label>
              <p className="text-xs text-gray-500 mb-2 dark:text-gray-400">
                Keywords used when AI categorization returns an invalid result. The system will match email content against these keywords to determine the correct ticket type.
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                {(formData.fallbackKeywords || []).map((keyword, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-md text-sm"
                  >
                    {keyword}
                    <button
                      type="button"
                      onClick={() => {
                        const newKeywords = [...(formData.fallbackKeywords || [])];
                        newKeywords.splice(index, 1);
                        setFormData({ ...formData, fallbackKeywords: newKeywords });
                      }}
                      className="text-primary-500 hover:text-primary-700 dark:hover:text-primary-200"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && keywordInput.trim()) {
                      e.preventDefault();
                      const newKeyword = keywordInput.trim().toLowerCase();
                      if (!formData.fallbackKeywords?.includes(newKeyword)) {
                        setFormData({
                          ...formData,
                          fallbackKeywords: [...(formData.fallbackKeywords || []), newKeyword],
                        });
                      }
                      setKeywordInput('');
                    }
                  }}
                  placeholder="Type a keyword and press Enter..."
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (keywordInput.trim()) {
                      const newKeyword = keywordInput.trim().toLowerCase();
                      if (!formData.fallbackKeywords?.includes(newKeyword)) {
                        setFormData({
                          ...formData,
                          fallbackKeywords: [...(formData.fallbackKeywords || []), newKeyword],
                        });
                      }
                      setKeywordInput('');
                    }
                  }}
                >
                  Add
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                Examples: quote, pricing, mpan, contract, renewal
              </p>
            </div>
          </div>
        )}

        {activeTab === 'fields' && (
          <FieldConfigurationPanel
            ticketType={ticketType}
            fieldGroups={fieldGroups}
            onChange={setFieldGroups}
          />
        )}

        {activeTab === 'ai' && (
          <ClassificationConfigEditor
            config={formData.classificationConfig || {}}
            onChange={(config: ClassificationConfig) =>
              setFormData({ ...formData, classificationConfig: config })
            }
            isCatchAll={formData.isCatchAll || false}
            onIsCatchAllChange={(value: boolean) =>
              setFormData({ ...formData, isCatchAll: value })
            }
            typeName={formData.name}
            typeKeywords={formData.fallbackKeywords || []}
          />
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? 'Saving...' : ticketType ? 'Update' : 'Create'}
        </Button>
      </div>
    </div>
  );
}

// Duplicate form component
interface DuplicateFormProps {
  ticketType: TicketType;
  onSubmit: (name: string, slug?: string) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function DuplicateForm({ ticketType, onSubmit, onCancel, isLoading }: DuplicateFormProps) {
  const [name, setName] = useState(`${ticketType.name} (Copy)`);
  const [slug, setSlug] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(name, slug || undefined);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Create a copy of "{ticketType.name}" with all its field configurations.
      </p>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          New Name *
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          New Slug (optional)
        </label>
        <input
          type="text"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="Auto-generated from name"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800"
        />
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading || !name.trim()}>
          {isLoading ? 'Duplicating...' : 'Duplicate'}
        </Button>
      </div>
    </form>
  );
}

// Main component
export function TicketTypesSettings() {
  const toast = useToast();
  // Use ref for toast to prevent loadTicketTypes from changing when toast reference changes
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingType, setEditingType] = useState<TicketType | null>(null);
  const [duplicatingType, setDuplicatingType] = useState<TicketType | null>(null);
  const [deletingType, setDeletingType] = useState<TicketType | null>(null);

  const loadTicketTypes = useCallback(async () => {
    try {
      setIsLoading(true);
      const types = await ticketTypesService.getTicketTypes({ includeFields: true });
      setTicketTypes(types);
    } catch (error: any) {
      toastRef.current.error('Error', error.message || 'Failed to load ticket types');
    } finally {
      setIsLoading(false);
    }
  }, []); // Empty dependency array - loadTicketTypes is now stable

  useEffect(() => {
    loadTicketTypes();
  }, [loadTicketTypes]);

  const handleCreate = async (data: CreateTicketTypeDto | UpdateTicketTypeDto, fieldGroups: FieldGroupConfig[]) => {
    try {
      setIsSaving(true);
      // Create ticket type with field groups
      const createData = {
        ...data as CreateTicketTypeDto,
        fieldGroups: fieldGroups.map((group) => ({
          key: group.key,
          name: group.name,
          description: group.description,
          icon: group.icon,
          displayLocation: group.displayLocation,
          isCollapsible: group.isCollapsible,
          isCollapsedByDefault: group.isCollapsedByDefault,
          sortOrder: group.sortOrder,
          fields: group.fields.map((field) => ({
            fieldDefinitionId: field.fieldDefinitionId,
            isRequired: field.isRequired,
            isVisible: field.isVisible,
            isVisibleInList: field.isVisibleInList,
            sortOrder: field.sortOrder,
          })),
        })),
      };
      await ticketTypesService.createTicketType(createData);
      toast.success('Success', 'Ticket type created successfully');
      setShowCreateModal(false);
      loadTicketTypes();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to create ticket type');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async (data: UpdateTicketTypeDto, fieldGroups: FieldGroupConfig[]) => {
    if (!editingType) return;
    try {
      setIsSaving(true);
      console.log('[Frontend] handleUpdate - fieldGroups count:', fieldGroups.length);
      console.log('[Frontend] handleUpdate - fieldGroups:', JSON.stringify(fieldGroups, null, 2));
      // Update ticket type with field groups
      const updateData = {
        ...data,
        fieldGroups: fieldGroups.map((group) => ({
          id: group.id.startsWith('group_') ? undefined : group.id, // New groups don't have real IDs yet
          key: group.key,
          name: group.name,
          description: group.description,
          icon: group.icon,
          displayLocation: group.displayLocation,
          isCollapsible: group.isCollapsible,
          isCollapsedByDefault: group.isCollapsedByDefault,
          sortOrder: group.sortOrder,
          fields: group.fields.map((field) => ({
            fieldDefinitionId: field.fieldDefinitionId,
            isRequired: field.isRequired,
            isVisible: field.isVisible,
            isVisibleInList: field.isVisibleInList,
            sortOrder: field.sortOrder,
          })),
        })),
      };
      console.log('[Frontend] handleUpdate - updateData.fieldGroups:', JSON.stringify(updateData.fieldGroups, null, 2));
      await ticketTypesService.updateTicketType(editingType.id, updateData);
      toast.success('Success', 'Ticket type updated successfully');
      setEditingType(null);
      loadTicketTypes();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to update ticket type');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = async (name: string, slug?: string) => {
    if (!duplicatingType) return;
    try {
      setIsSaving(true);
      await ticketTypesService.duplicateTicketType(duplicatingType.id, name, slug);
      toast.success('Success', 'Ticket type duplicated successfully');
      setDuplicatingType(null);
      loadTicketTypes();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to duplicate ticket type');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingType) return;
    try {
      setIsSaving(true);
      await ticketTypesService.deleteTicketType(deletingType.id);
      toast.success('Success', 'Ticket type deleted successfully');
      setDeletingType(null);
      loadTicketTypes();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to delete ticket type');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (ticketType: TicketType) => {
    try {
      await ticketTypesService.updateTicketType(ticketType.id, {
        isActive: !ticketType.isActive,
      });
      toast.success('Success', `Ticket type ${ticketType.isActive ? 'deactivated' : 'activated'}`);
      loadTicketTypes();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to update ticket type');
    }
  };

  const handleSetDefault = async (ticketType: TicketType) => {
    try {
      await ticketTypesService.updateTicketType(ticketType.id, { isDefault: true });
      toast.success('Success', `${ticketType.name} is now the default ticket type`);
      loadTicketTypes();
    } catch (error: any) {
      toast.error('Error', error.message || 'Failed to set default');
    }
  };

  // Filter ticket types by search
  const filteredTypes = ticketTypes.filter(
    (type) =>
      type.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      type.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      type.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Ticket Types</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure different types of tickets with custom fields and workflows
          </p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Ticket Type
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search ticket types..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      {/* Ticket Types List */}
      {filteredTypes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4 dark:text-gray-500" />
          <p className="text-gray-500 dark:text-gray-400">
            {searchQuery ? 'No ticket types found matching your search' : 'No ticket types yet'}
          </p>
          {!searchQuery && (
            <Button onClick={() => setShowCreateModal(true)} className="mt-4">
              Create your first ticket type
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTypes.map((ticketType) => {
            const Icon = getIconComponent(ticketType.icon);
            return (
              <div
                key={ticketType.id}
                className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-sm transition-shadow"
              >
                {/* Icon */}
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: `${ticketType.color || '#6b7280'}20`,
                    color: ticketType.color || '#6b7280',
                  }}
                >
                  <Icon className="w-5 h-5" />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
                      {ticketType.name}
                    </h3>
                    {ticketType.isDefault && (
                      <Badge variant="info" size="sm">
                        Default
                      </Badge>
                    )}
                    {!ticketType.isActive && (
                      <Badge variant="secondary" size="sm">
                        Inactive
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-mono flex-shrink-0 whitespace-nowrap">{ticketType.slug}</span>
                    {ticketType.ticketPrefix && (
                      <span className="flex-shrink-0 whitespace-nowrap">Prefix: {ticketType.ticketPrefix}</span>
                    )}
                    {ticketType.description && (
                      <span className="truncate">{ticketType.description}</span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleSetDefault(ticketType)}
                    disabled={ticketType.isDefault}
                    className={`p-2 rounded-lg transition-colors ${
                      ticketType.isDefault
                        ? 'text-yellow-500 cursor-default'
                        : 'text-gray-400 hover:text-yellow-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-500'
                    }`}
                    title={ticketType.isDefault ? 'Default type' : 'Set as default'}
                  >
                    {ticketType.isDefault ? (
                      <Star className="h-4 w-4 fill-current" />
                    ) : (
                      <StarOff className="h-4 w-4" />
                    )}
                  </button>

                  <button
                    onClick={() => handleToggleActive(ticketType)}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors dark:text-gray-500"
                    title={ticketType.isActive ? 'Deactivate' : 'Activate'}
                  >
                    {ticketType.isActive ? (
                      <ToggleRight className="h-4 w-4 text-green-500" />
                    ) : (
                      <ToggleLeft className="h-4 w-4" />
                    )}
                  </button>

                  <button
                    onClick={() => setDuplicatingType(ticketType)}
                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors dark:text-gray-500"
                    title="Duplicate"
                  >
                    <Copy className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => setEditingType(ticketType)}
                    className="p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors dark:text-gray-500"
                    title="Edit"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>

                  <button
                    onClick={() => setDeletingType(ticketType)}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors dark:text-gray-500"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create Ticket Type"
        size="xl"
      >
        <div className="h-[70vh]">
          <TicketTypeEditor
            onSubmit={handleCreate}
            onCancel={() => setShowCreateModal(false)}
            isLoading={isSaving}
          />
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingType}
        onClose={() => setEditingType(null)}
        title="Edit Ticket Type"
        size="xl"
      >
        {editingType && (
          <div className="h-[70vh]">
            <TicketTypeEditor
              ticketType={editingType}
              onSubmit={handleUpdate}
              onCancel={() => setEditingType(null)}
              isLoading={isSaving}
            />
          </div>
        )}
      </Modal>

      {/* Duplicate Modal */}
      <Modal
        isOpen={!!duplicatingType}
        onClose={() => setDuplicatingType(null)}
        title="Duplicate Ticket Type"
      >
        {duplicatingType && (
          <DuplicateForm
            ticketType={duplicatingType}
            onSubmit={handleDuplicate}
            onCancel={() => setDuplicatingType(null)}
            isLoading={isSaving}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingType}
        onClose={() => setDeletingType(null)}
        title="Delete Ticket Type"
      >
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-400">
            Are you sure you want to delete <strong>{deletingType?.name}</strong>? This action
            cannot be undone.
          </p>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              Warning: You cannot delete a ticket type that has existing tickets. You must migrate
              or delete those tickets first.
            </p>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="outline"
              onClick={() => setDeletingType(null)}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={isSaving}>
              {isSaving ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
